import { useDispatch, useSelector } from 'react-redux';
import { Text, Button, Col, FlexboxGrid } from 'rsuite';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { DASHBOARD_ACTIONS_ENUM } from '../../../common/enums';
import {
  setPreSelectedTaxPeriod,
  updateTaxFlowData,
} from '../../../store/slices/tax/taxSlice';
import usePlaceNavigate from '../../../hooks/usePlaceNavigate';
import { setTaxSteps } from '../../../store/slices/flow/flowThunks';
import {
  selectInitiatingRenewFlow,
  selectInitiatingRenewFlowError,
  selectRenewApplicationNumber,
} from '../../../store/slices/renew/renewSelectors';
import {
  initiateBulkRenewFlow,
  initiateRenewFlow,
} from '../../../store/slices/renew/renewThunks';
import { isNullUndefinedOrEmptyString } from '../../../common/utils';
import IconIndicator from '../../user-feedback/IconIndicator';
import Notice from '../../user-feedback/Notice';

const ActionItem = ({ action }) => {
  const {
    type,
    label,
    dueDate,
    startDate, // only when type is tax related
    endDate, // only when type is tax related
    periodLabel,
    selectedLicense,
  } = action;

  const {
    address = '',
    licenseNumber = '',
    applicationNumber = '',
  } = selectedLicense || {};

  const dispatch = useDispatch();
  const placeNavigate = usePlaceNavigate();

  const [showInitiateRenewFlowError, setShowInitiateRenewFlowError] =
    useState(false);
  const initiatingRenewFlow = useSelector(selectInitiatingRenewFlow);
  const initiatingRenewFlowError = useSelector(selectInitiatingRenewFlowError);
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const applicationBeingRenewed = useSelector(selectRenewApplicationNumber);

  useEffect(() => {
    if (
      !initiatingRenewFlow &&
      applicationNumber === applicationBeingRenewed &&
      type === DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW &&
      !isNullUndefinedOrEmptyString(initiatingRenewFlowError)
    ) {
      setShowInitiateRenewFlowError(true);
      setTimeout(() => {
        setShowInitiateRenewFlowError(false);
      }, 10000); // 10 seconds
    } else {
      setShowInitiateRenewFlowError(false);
    }
  }, [
    initiatingRenewFlow,
    applicationBeingRenewed,
    applicationNumber,
    initiatingRenewFlow,
  ]);

  useEffect(() => {
    if (type === DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW) {
      if (
        initiatingRenewFlow &&
        applicationNumber === applicationBeingRenewed
      ) {
        setShowLoadingIndicator(true);
      } else {
        setShowLoadingIndicator(false);
      }
    }
  }, [initiatingRenewFlow, applicationBeingRenewed, applicationNumber]);

  const actionButtonText = useMemo(() => {
    switch (type) {
      case DASHBOARD_ACTIONS_ENUM.BULK_TAX:
        return 'Pay Tax';
      case DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW:
        return 'Renew';
      case DASHBOARD_ACTIONS_ENUM.BULK_RENEW:
        return 'Bulk Renew';
    }
  }, [type]);

  const onFlowStepsMapped = (path) => {
    placeNavigate(`/${path}`);
  };

  const bulkTaxClick = () => {
    // Save start and end date associated with the clicked action into redux
    // If there is a selectedLicense skip the select properties step
    if (selectedLicense) {
      const selectedData = [
        {
          startDate: startDate,
          endDate: endDate,
          selected: true,
          year: dayjs(startDate).year(),
          label: periodLabel,
          licenses: [
            {
              address: address,
              selected: true,
              license: licenseNumber,
            },
          ],
        },
      ];

      dispatch(updateTaxFlowData({ selectedLicenses: selectedData }));
      dispatch(setTaxSteps(onFlowStepsMapped, true, `/dashboard`));
    } else {
      dispatch(
        setPreSelectedTaxPeriod({ startDate: startDate, endDate: endDate })
      );
      dispatch(setTaxSteps(onFlowStepsMapped, false));
    }
  };

  const singleRenewClick = useCallback(() => {
    if (!initiatingRenewFlow) {
      dispatch(
        initiateRenewFlow(applicationNumber, `/dashboard`, onFlowStepsMapped)
      );
    }
  }, [applicationNumber, initiatingRenewFlow]);

  const bulkRenewClick = useCallback(() => {
    if (!initiatingRenewFlow) {
      dispatch(initiateBulkRenewFlow(`/dashboard`, onFlowStepsMapped));
    }
  }, [initiatingRenewFlow]);

  const actionClick = useCallback(
    (e) => {
      e.target.blur();
      setShowLoadingIndicator(true);
      switch (type) {
        case DASHBOARD_ACTIONS_ENUM.BULK_TAX:
          bulkTaxClick();
          break;
        case DASHBOARD_ACTIONS_ENUM.SINGLE_RENEW:
          singleRenewClick();
          break;
        case DASHBOARD_ACTIONS_ENUM.BULK_RENEW:
          bulkRenewClick();
          break;
      }
    },
    [type]
  );

  return (
    <div role="grid">
      <FlexboxGrid
        role="row"
        align="middle"
        className="pv-12 pl-20"
        style={{ paddingRight: '13px' }}
      >
        <FlexboxGrid.Item
          as={Col}
          colspan={24}
          lg={16}
        >
          <Text>{label}</Text>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          colspan={24}
          lg={4}
        >
          <Text>{dayjs(dueDate).format('MM/DD/YYYY')}</Text>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item
          as={Col}
          colspan={24}
          lg={4}
        >
          <Button
            className="btn-primary"
            style={{ width: '100%' }}
            onClick={(e) => {
              actionClick(e);
            }}
          >
            {actionButtonText}
            {showLoadingIndicator && (
              <IconIndicator
                size={16}
                color="#FFFFFF"
                style={{ marginLeft: '5px' }}
              />
            )}
          </Button>
        </FlexboxGrid.Item>
        {showInitiateRenewFlowError && (
          <FlexboxGrid.Item
            as={Col}
            xs={24}
            style={{ marginTop: '10px' }}
          >
            <Notice message={initiatingRenewFlowError} />
          </FlexboxGrid.Item>
        )}
      </FlexboxGrid>
    </div>
  );
};

export default ActionItem;
